import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const STORAGE_KEY = 'playroots:wishlist:v1';

function message(key, fallback, title = '') {
  return (typeof Theme !== 'undefined' && Theme.translations[key] || fallback).replace('{{ title }}', title).replace('__TITLE__', title);
}

function readWishlist() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(saved) ? saved.filter((item) => item && item.id && item.handle) : [];
  } catch {
    return [];
  }
}

function writeWishlist(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    return true;
  } catch {
    announce(message('wishlistSaveError', 'Wishlist could not be saved in this browser.'));
    return false;
  }
}

let announcementTimer;

function announce(message) {
  document.querySelectorAll('[data-wishlist-status]').forEach((region) => {
    region.textContent = message;
  });
  const toast = document.querySelector('[data-wishlist-toast]');
  if (toast) {
    toast.hidden = false;
    clearTimeout(announcementTimer);
    announcementTimer = setTimeout(() => { toast.hidden = true; }, 8000);
  }
}

function productFromButton(button) {
  const product = {
    id: button.dataset.wishlistId,
    handle: button.dataset.wishlistHandle,
    title: button.dataset.wishlistTitle,
    url: button.dataset.wishlistUrl,
    image: button.dataset.wishlistImage,
    price: button.dataset.wishlistPrice,
    variant: button.dataset.wishlistVariant,
  };

  if (button.classList.contains('wishlist-toggle--product')) {
    const variantInput = button.closest('.shopify-section')?.querySelector('product-form-component input[name="id"]');
    if (variantInput?.value) product.variant = variantInput.value;
  }

  return product;
}

function setToggleState(button, saved) {
  const pressed = String(saved);
  const ariaLabel = saved ? button.dataset.wishlistAriaSaved : button.dataset.wishlistAriaUnsaved;
  if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
  if (button.getAttribute('aria-label') !== ariaLabel) button.setAttribute('aria-label', ariaLabel);
  const label = button.querySelector('[data-wishlist-toggle-label]');
  const labelText = saved ? button.dataset.wishlistLabelSaved : button.dataset.wishlistLabelUnsaved;
  if (label && label.textContent !== labelText) label.textContent = labelText;
}

function refreshToggles(items) {
  const savedIds = new Set(items.map((item) => String(item.id)));
  document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
    setToggleState(button, savedIds.has(String(button.dataset.wishlistId)));
  });
}

function refreshSavedState(items) {
  refreshToggles(items);
  document.querySelectorAll('[data-wishlist-count-header]').forEach((badge) => {
    const count = String(items.length);
    const hidden = items.length === 0;
    if (badge.textContent !== count) badge.textContent = count;
    if (badge.hidden !== hidden) badge.hidden = hidden;
    const link = badge.closest('a, button');
    const ariaLabel = `${link?.dataset.navLabel || 'Wishlist'}, ${items.length} saved`;
    if (link && link.getAttribute('aria-label') !== ariaLabel) link.setAttribute('aria-label', ariaLabel);
  });
}

let savedStateRefreshScheduled = false;

function scheduleSavedStateRefresh() {
  if (savedStateRefreshScheduled) return;
  savedStateRefreshScheduled = true;
  requestAnimationFrame(() => {
    savedStateRefreshScheduled = false;
    refreshSavedState(readWishlist());
  });
}

const SAVED_STATE_SELECTOR = '[data-wishlist-toggle], [data-wishlist-count-header]';

function containsSavedState(node) {
  return node instanceof Element && (node.matches(SAVED_STATE_SELECTOR) || node.querySelector(SAVED_STATE_SELECTOR));
}

// Section rendering also morphs retained buttons and text back to their server defaults.
// Restore from storage for every rendering path, without rebuilding the wishlist grid.
// Idempotent writes above let our own observed changes settle instead of looping.
const wishlistStateObserver = new MutationObserver((records) => {
  if (records.some((record) => {
    const target = record.target instanceof Element ? record.target : record.target.parentElement;
    if (target?.closest(SAVED_STATE_SELECTOR)) return true;
    if (record.type === 'attributes') {
      return target?.matches('a, button') && target.querySelector('[data-wishlist-count-header]');
    }
    return record.type === 'childList' && [...record.addedNodes].some(containsSavedState);
  })) {
    scheduleSavedStateRefresh();
  }
});

wishlistStateObserver.observe(document.documentElement, {
  childList: true,
  characterData: true,
  subtree: true,
  attributes: true,
  attributeFilter: [
    'aria-pressed', 'aria-label', 'hidden', 'data-nav-label',
    'data-wishlist-toggle', 'data-wishlist-id', 'data-wishlist-count-header',
    'data-wishlist-aria-saved', 'data-wishlist-aria-unsaved',
    'data-wishlist-label-saved', 'data-wishlist-label-unsaved',
  ],
});

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function safeStoreUrl(path) {
  try {
    const url = new URL(path, window.location.origin);
    return url.origin === window.location.origin ? url.href : '#';
  } catch {
    return '#';
  }
}

function safeImageUrl(path) {
  try {
    const url = new URL(path, window.location.origin);
    if (url.origin === window.location.origin) return url.href;
    return url.protocol === 'https:' && url.hostname.endsWith('.shopify.com') ? url.href : '';
  } catch {
    return '';
  }
}

const productCache = new Map();
let wishlistRenderVersion = 0;

function getProduct(handle) {
  if (!productCache.has(handle)) {
    const root = window.Shopify?.routes?.root || '/';
    const request = fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    }).then(async (response) => {
      if (!response.ok) throw new Error('Could not check availability.');
      return response.json();
    }).catch((error) => {
      if (productCache.get(handle) === request) productCache.delete(handle);
      throw error;
    });
    productCache.set(handle, request);
  }
  return productCache.get(handle);
}

function formatPrice(cents) {
  const currency = window.Shopify?.currency?.active || 'EGP';
  if (typeof Theme !== 'undefined' && Theme.moneyFormat && currency === Theme.currency) {
    const formats = {
      amount: [2, ',', '.'], amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','], amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'], amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', '.'], amount_with_period_and_space_separator: [2, ' ', '.'],
    };
    return Theme.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (placeholder, token) => {
      if (!formats[token]) return placeholder;
      const [precision, thousands, decimal] = formats[token];
      const [whole, fraction] = (Number(cents) / 100).toFixed(precision).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    });
  }
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(Number(cents) / 100);
  } catch {
    return `${(Number(cents) / 100).toFixed(2)} ${currency}`;
  }
}

function createWishlistCard(item, product, page) {
  const card = element('product-card', 'product-card wishlist-card');
  card.dataset.productId = String(item.id);
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const savedVariant = variants.find((variant) => String(variant.id) === String(item.variant));
  const selectedVariant = savedVariant || (!item.variant && variants.length === 1 ? variants[0] : null);
  const content = element('div', 'product-card__content layout-panel-flex layout-panel-flex--column product-grid__card wishlist-card__content');
  const media = element('div', 'wishlist-card__media');
  const imageLink = element('a', 'wishlist-card__image');
  const url = new URL(safeStoreUrl(item.url || `/products/${item.handle}`), window.location.origin);
  if (savedVariant) url.searchParams.set('variant', String(savedVariant.id));
  const productUrl = url.href;
  imageLink.href = productUrl;
  imageLink.setAttribute('ref', 'productCardLink');
  imageLink.setAttribute('aria-label', item.title || 'View product');

  const imagePath = selectedVariant?.featured_image?.src || product?.featured_image || product?.images?.[0] || item.image;
  const imageUrl = safeImageUrl(imagePath);
  if (imageUrl) {
    const image = document.createElement('img');
    const resized = (width) => {
      const source = new URL(imageUrl);
      source.searchParams.set('width', String(width));
      return source.href;
    };
    image.src = resized(600);
    image.srcset = [180, 300, 450, 600, 900].map((width) => `${resized(width)} ${width}w`).join(', ');
    image.sizes = '(max-width: 749px) calc((100vw - 48px) / 2), (max-width: 1100px) calc((100vw - 80px) / 3), 300px';
    image.width = 600;
    image.height = 600;
    image.alt = item.title || '';
    image.loading = 'lazy';
    imageLink.append(image);
  }

  const removeButton = element('button', 'wishlist-toggle wishlist-toggle--card wishlist-card__remove');
  removeButton.type = 'button';
  removeButton.dataset.wishlistToggle = '';
  removeButton.dataset.wishlistId = String(item.id);
  removeButton.dataset.wishlistHandle = item.handle;
  removeButton.dataset.wishlistTitle = item.title || 'Product';
  removeButton.dataset.wishlistUrl = item.url || `/products/${item.handle}`;
  removeButton.dataset.wishlistImage = item.image || '';
  removeButton.dataset.wishlistPrice = item.price || '';
  removeButton.dataset.wishlistVariant = String(item.variant || '');
  removeButton.dataset.wishlistLabelSaved = page.dataset.removeLabel;
  removeButton.dataset.wishlistLabelUnsaved = page.dataset.saveLabel;
  removeButton.dataset.wishlistAriaSaved = page.dataset.removeAria;
  removeButton.dataset.wishlistAriaUnsaved = page.dataset.saveAria;
  removeButton.setAttribute('aria-pressed', 'true');
  removeButton.setAttribute('aria-label', removeButton.dataset.wishlistAriaSaved);
  const icon = document.querySelector('[data-wishlist-heart-template]');
  if (icon) removeButton.append(icon.content.cloneNode(true));

  const badges = element('div', 'wishlist-card__badges');
  const title = element('a', 'wishlist-card__title', product?.title || item.title || 'Product');
  title.href = productUrl;
  const price = element('p', 'wishlist-card__price', product ? formatPrice(selectedVariant?.price ?? product.price) : item.price || '');
  const actions = element('div', 'wishlist-card__actions');
  const addButton = element('button', 'button button--primary wishlist-card__add');
  addButton.type = 'button';
  addButton.dataset.wishlistAdd = '';

  if (!product) {
    addButton.textContent = page.dataset.checkingLabel;
    addButton.disabled = true;
    addButton.dataset.availabilityPending = '';
  } else {
    if (selectedVariant?.available) {
      addButton.textContent = page.dataset.addLabel;
      addButton.dataset.variant = String(selectedVariant.id);
      addButton.disabled = false;
    } else {
      const anyAvailable = variants.some((variant) => variant.available);
      addButton.textContent = anyAvailable ? page.dataset.optionUnavailableLabel : page.dataset.soldOutLabel;
      addButton.disabled = true;
      if (!anyAvailable || savedVariant) badges.append(element('span', 'product-badges__badge product-badges__badge--rectangle', addButton.textContent));
      if (anyAvailable) {
        const choose = element('a', 'button button--secondary', page.dataset.chooseLabel);
        choose.href = productUrl;
        actions.append(choose);
      }
    }
  }

  media.append(imageLink, removeButton, badges);
  actions.append(addButton);
  content.append(media, title);
  if (selectedVariant && variants.length > 1) content.append(element('p', 'wishlist-card__variant', selectedVariant.public_title || selectedVariant.title));
  content.append(price, actions);
  card.append(content);
  return card;
}

function renderWishlist(items) {
  const page = document.querySelector('[data-wishlist-page]');
  if (!page) return;

  const grid = page.querySelector('[data-wishlist-grid]');
  const empty = page.querySelector('[data-wishlist-empty]');
  const count = page.querySelector('[data-wishlist-count]');
  const renderVersion = ++wishlistRenderVersion;
  grid.replaceChildren();
  count.textContent = items.length ? `(${items.length})` : '';
  empty.hidden = items.length > 0;

  const cards = items.map((item) => {
    const card = createWishlistCard(item, null, page);
    grid.append(card);
    return { item, card };
  });

  Promise.all(cards.map(async ({ item, card }) => {
    try {
      const product = await getProduct(item.handle);
      if (renderVersion !== wishlistRenderVersion || !card.isConnected) return;
      card.replaceWith(createWishlistCard(item, product, page));
    } catch {
      if (renderVersion !== wishlistRenderVersion || !card.isConnected) return;
      const button = card.querySelector('[data-wishlist-add]');
      button.textContent = page.dataset.availabilityErrorLabel;
      button.disabled = true;
      button.removeAttribute('data-availability-pending');
      const retry = element('button', 'button button--secondary', page.dataset.retryLabel);
      retry.type = 'button';
      retry.dataset.wishlistRetry = '';
      card.querySelector('.wishlist-card__actions').append(retry);
    }
  }));
}

function updateUI(items) {
  refreshSavedState(items);
  renderWishlist(items);
}

document.addEventListener('click', async (event) => {
  if (event.target.closest('[data-wishlist-retry]')) {
    renderWishlist(readWishlist());
    return;
  }
  const toggle = event.target.closest('[data-wishlist-toggle]');
  if (toggle) {
    event.preventDefault();
    event.stopPropagation();
    const item = productFromButton(toggle);
    const items = readWishlist();
    const existingIndex = items.findIndex((saved) => String(saved.id) === String(item.id));
    if (existingIndex >= 0) {
      items.splice(existingIndex, 1);
    } else {
      items.unshift(item);
    }
    if (!writeWishlist(items)) return;
    updateUI(items);
    announce(existingIndex >= 0 ? message('wishlistRemoved', '{{ title }} removed from your wishlist.', item.title) : message('wishlistSaved', '{{ title }} saved to your wishlist.', item.title));
    return;
  }

  const removeButton = event.target.closest('[data-wishlist-remove]');
  if (removeButton) {
    const items = readWishlist().filter((item) => String(item.id) !== removeButton.dataset.wishlistRemove);
    if (!writeWishlist(items)) return;
    updateUI(items);
    announce(message('wishlistRemoved', '{{ title }} removed from your wishlist.', 'Product'));
    return;
  }

  const addButton = event.target.closest('[data-wishlist-add]');
  if (!addButton || addButton.disabled) return;

  const variantId = addButton.dataset.variant;
  if (!variantId) {
    announce(message('productUnavailable', 'This product is currently unavailable.'));
    return;
  }

  addButton.disabled = true;
  addButton.textContent = message('adding', 'Adding…');
  const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);
  const formData = new FormData();
  formData.set('id', variantId);
  formData.set('quantity', '1');
  if (sections.length) formData.set('sections', sections.join(','));
  const pending = CartLinesUpdateEvent.createPromise();
  addButton.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: pending.promise,
    })
  );

  let added = false;
  try {
    const root = window.Shopify?.routes?.root || '/';
    const addResponse = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const addResult = await addResponse.json();
    if (!addResponse.ok || addResult.status) throw new Error(addResult.description || addResult.message || 'Could not add this product.');
    added = true;

    const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await cartResponse.json();
    if (!cartResponse.ok) throw new Error('Could not refresh the cart.');
    pending.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'wishlist',
        sourceId: 'wishlist',
        itemCount: 1,
        productId: addResult.product_id,
        sections: addResult.sections,
        didError: false,
      },
    });
    addButton.textContent = message('addedToCart', 'Added to cart');
    announce(message('addedToCart', 'Added to cart'));
  } catch (error) {
    pending.reject(error);
    addButton.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
    addButton.textContent = added ? message('cartRefreshError', 'Item added. Please open your cart to review it.') : error.message || message('addToCartError', 'Could not add this product. Please try again.');
    announce(addButton.textContent);
  } finally {
    setTimeout(() => {
      if (!addButton.isConnected) return;
      addButton.disabled = false;
      addButton.textContent = message('addToCart', 'Add to cart');
    }, 1800);
  }
});


updateUI(readWishlist());
window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY || event.key === null) updateUI(readWishlist());
});
window.addEventListener('pageshow', (event) => {
  if (event.persisted) updateUI(readWishlist());
});
